import {
  createInfiniteQuery,
  type InfiniteQueryHookOptions,
} from 'react-query-kit';
import { type InfiniteData, keepPreviousData } from '@tanstack/react-query';

import type { MyQuizsetCursorSearchReq } from '@/api/gen/_model/myQuizsetCursorSearchReq';
import type { SearchQuizsetRes } from '@/api/gen/_model/searchQuizsetRes';
import { searchMyQuizsetsByCursor } from '@/api/gen/quiz-user/quiz-user.gen';
import {
  isRecoverableCursorError,
  useCursorQueryRecovery,
} from '@/shared/hooks/useCursorQueryRecovery';

/**
 * 내 퀴즈 목록을 stale 로 만드는 write 가 이 모듈 밖에도 있어(폴더가 원본 퀴즈를 공개로
 * 전환한다) 무효화용 키를 내보낸다. **접두사**이고 프로필 목록(`['quizsets/search']`)까지
 * 함께 걸린다 — 공개 전환은 그 목록에도 반영돼야 하므로 의도한 범위다.
 *
 * ⚠️ 이 상수를 `useFetchMyQuizesQuery.getKey()` 로 파생하지 않는다. 그러면 값이 아래의 좁은
 * 키가 되어 프로필 목록이 무효화 대상에서 빠진다 — `invalidateQueries` 의 접두사 매칭은
 * 한 방향이다.
 *
 * 자료실은 혼합 검색(`libraryKeys`)으로 떠났으므로 이 키에 걸리지 않는다. 자료실까지
 * 갱신해야 하는 write 는 `libraryKeys.all` 도 함께 무효화한다.
 */
export const myQuizListKey = ['quizsets/search'] as const;

const MAX_EMPTY_CURSOR_CONTINUATIONS = 3;

export type MyQuizCursorVariables = Omit<MyQuizsetCursorSearchReq, 'cursor'>;

export interface MyQuizCursorPage {
  items: SearchQuizsetRes[];
  nextCursor?: string;
  hasNext: boolean;
  totalCount?: number;
}

type MyQuizCursorQueryOptions = InfiniteQueryHookOptions<
  MyQuizCursorPage,
  Error,
  InfiniteData<MyQuizCursorPage, string | undefined>,
  MyQuizCursorVariables,
  string | undefined
>;

/**
 * 커서 한 페이지를 읽는다.
 *
 * 서버가 필터로 비운 중간 페이지를 돌려줄 수 있어 **최대 3회까지 이어 받는다.** 그러지 않으면
 * 목록은 비어 보이는데 `hasNext` 만 true 인 상태가 된다. `totalCount` 는 첫 응답 값을 보존한다.
 */
export const fetchMyQuizCursorPage = async (
  variables: MyQuizCursorVariables,
  cursor?: string,
  signal?: AbortSignal,
): Promise<MyQuizCursorPage> => {
  let response = await searchMyQuizsetsByCursor(
    { ...variables, cursor },
    undefined,
    signal,
  );
  const initialTotalCount = response.totalCount;

  for (
    let skippedCount = 0;
    skippedCount < MAX_EMPTY_CURSOR_CONTINUATIONS;
    skippedCount += 1
  ) {
    if (response.data.length > 0 || !response.hasNext || !response.nextCursor) {
      break;
    }
    response = await searchMyQuizsetsByCursor(
      { ...variables, cursor: response.nextCursor },
      undefined,
      signal,
    );
  }

  return {
    items: response.data,
    nextCursor: response.nextCursor,
    hasNext: response.hasNext,
    totalCount: response.totalCount ?? initialTotalCount,
  };
};

/**
 * 원본 쿼리. 키·옵션을 직접 다뤄야 하는 곳(테스트 등)만 쓰고, 화면은 아래 `useFetchMyQuizes` 를 쓴다.
 */
export const useFetchMyQuizesQuery = createInfiniteQuery<
  MyQuizCursorPage,
  MyQuizCursorVariables,
  Error,
  string | undefined
>({
  // 프로필 목록과 접두사를 공유하되, 이 쿼리만의 세그먼트로 캐시를 가른다.
  queryKey: [...myQuizListKey, 'my', 'cursor'],
  fetcher: (variables, { pageParam, signal }) =>
    fetchMyQuizCursorPage(variables, pageParam, signal),
  getNextPageParam: lastPage =>
    lastPage.hasNext ? lastPage.nextCursor : undefined,
  initialPageParam: undefined,
  placeholderData: keepPreviousData,
  throwOnError: error => !isRecoverableCursorError(error),
});

/**
 * 커서가 만료되면(`INVALID_CURSOR`·`EXPIRED_CURSOR`·`CURSOR_QUERY_MISMATCH`) 에러로 던지지 않고
 * 한 번만 다시 읽는다. 복구하는 동안에도 목록이 살아 있게 `isFetchingNextPage` 로 묶어 준다.
 */
export const useFetchMyQuizes = (options?: MyQuizCursorQueryOptions) => {
  const query =
    useFetchMyQuizesQuery<InfiniteData<MyQuizCursorPage, string | undefined>>(
      options,
    );
  const { isRecovering } = useCursorQueryRecovery({
    isError: query.isError,
    error: query.error,
    hasLoadedPages:
      !query.isPlaceholderData && (query.data?.pages.length ?? 0) > 0,
    refetch: query.refetch,
  });

  return {
    ...query,
    isFetchingNextPage: query.isFetchingNextPage || isRecovering,
  };
};
